import { formatDate } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { debounceTimeAfterFirst } from "@app/_helpers/debounceAfterTime";
import { pushError } from "@app/_helpers/globalErrorHandler";
import { isBreakRuleError, isWorkingHoursError } from "@app/_helpers/is-error-object";
import { hasPermissionByKey } from "@app/_helpers/permission";
import { toPromise } from "@app/_helpers/promise";
import {
	asyncWrapTimeout,
	coerceTimeDuration,
	createRxValue,
	fromRxValue,
	getActiveSchedule,
	parseScheduleStats,
	timeDurationSeconds,
	timeFormatToDuration,
} from "@app/_helpers/utils";
import { ClockService } from "@app/_services/clock.service";
import { AppService } from "@app/app.service";
import { RecordToolbarService } from "@app/shared/record-toolbar/record-toolbar.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import {
	ApplicationSettingsQuery,
	ComegoQuery,
	ComegoService,
	ComegoTime,
	ComegoTimeType,
	Logger,
	UserSettingsQuery,
} from "@timeghost/web-api";
import {
	addMinutes,
	endOfWeek,
	isBefore,
	isSameDay,
	isSameMinute,
	isValid,
	isWithinInterval,
	roundToNearestMinutes,
	startOfDay,
	startOfMinute,
	startOfToday,
	startOfWeek,
} from "date-fns/esm";
import { capitalize } from "lodash-es";
import { NgxTippyProps } from "ngx-tippy-wrapper";
import { combineLatest, combineLatestWith, map, startWith, switchMap } from "rxjs";
import { firstBy } from "thenby";
import {
	ComeAndGoAbsenceDialogComponent,
	WorkingHoursAbsenceData,
} from "../come-and-go-absence-dialog/come-and-go-absence-dialog.component";
import {
	ComeAndGoCreateDialogComponent,
	WorkingHoursCreateData,
} from "../come-and-go-create-dialog/come-and-go-create-dialog.component";
import { ComeAndGoMultiUpdateDialogComponent } from "../come-and-go-multi-update-dialog/come-and-go-update-dialog.component";

const STORE_KEY = "cag";
// Cooldown to prevent spamming the API with duplicate requests
const ACTION_COOLDOWN_AFTER_CLICK_MS = 1000;
const log = new Logger("ComeAndGoBoxComponent");
const typeNameMap = {
	pause: "comego.state.pause",
	play: "comego.check_in",
};
@UntilDestroy()
@Component({
	selector: "tg-come-and-go-box",
	templateUrl: "./come-and-go-box.component.html",
	styleUrls: ["./come-and-go-box.component.scss"],
})
export class ComeAndGoBoxComponent implements OnInit, OnDestroy {
	readonly loading$ = createRxValue(false);
	/** True for ACTION_COOLDOWN_AFTER_CLICK_MS after each handle(); keeps UI in sync with debounced activeTime$ and blocks spam. */
	private readonly actionCooldown = createRxValue(false);
	readonly actionCooldown$ = this.actionCooldown.value$;
	private cooldownClearHandle: ReturnType<typeof setTimeout> | null = null;
	constructor(
		private appSettings: ApplicationSettingsQuery,
		private appService: AppService,
		private comego: ComegoService,
		private comegoQuery: ComegoQuery,
		private recordService: RecordToolbarService,
		private userSettingsQuery: UserSettingsQuery,
		private dialog: MatDialog,
		private clock: ClockService
	) {}
	readonly now$ = this.clock.now$;
	readonly workspace$canCreate = this.userSettingsQuery
		.select()
		.pipe(map((x) => hasPermissionByKey(x, "groupsCanComegoCreateTimes" as any)));
	readonly workspace$canUpdate = this.userSettingsQuery
		.select()
		.pipe(map((x) => hasPermissionByKey(x, "groupsCanComegoUpdateTimes" as any)));
	readonly workspace$canCreateAbsences = this.userSettingsQuery
		.select()
		.pipe(map((x) => hasPermissionByKey(x, "groupsCanCreateAbsences" as any)));
	readonly activeTime$ = fromRxValue(
		this.comegoQuery.selectAllFromCurrentUser({ filterBy: (x: any) => !x.end && !x.deleted }).pipe(
			combineLatestWith(this.userSettingsQuery.select()),
			map(([x, user]) => x?.find((t) => t.user.id === user.id) || null),
			debounceTimeAfterFirst(200)
		),
		null,
		null
	);
	readonly activeTime$times = combineLatest([
		this.activeTime$.asObservable(),
		this.comegoQuery.selectAll(),
		this.userSettingsQuery.select(),
	]).pipe(
		map(([active, times, user]) => {
			if (!active) return null;
			const start = new Date(active.start);
			return times
				.filter((x) => x.user.id === user.id && isSameDay(start, new Date(x.start)))
				.map(({ start, end, ...x }) => ({ start: new Date(start), end: (end && new Date(end)) || null, ...x }));
		})
	);
	readonly activeTime$stats = combineLatest([this.activeTime$.asObservable(), this.now$, this.activeTime$times]).pipe(
		map(([time, now, times]) => {
			if (!time) return null;
			const start = new Date(time.start);
			const gapRanges = times || [];
			const last = gapRanges[gapRanges.length - 1];
			const duration = timeDurationSeconds(time, now);
			return {
				start,
				last,
				paused: time?.type === "pause",
				gaps: gapRanges,
				duration,
				now: now.getTime(),
			};
		})
	);
	readonly scheduleType = createRxValue(false); // weekly = true, daily = false
	readonly isActive$ = this.activeTime$.asObservable().pipe(map((x) => !!x));
	readonly hasOverlappingEntry$ = combineLatest([
		this.now$,
		this.comegoQuery.selectAllFromCurrentUser({ filterBy: (x) => isSameDay(new Date(x.start), new Date()) }),
	]).pipe(
		map(([now, comegos]) => {
			return comegos.some((entry) => {
				if (entry.type !== "work" && entry.type !== "pause") return false;
				const start = new Date(entry.start);
				const end = entry.end ? new Date(entry.end) : null;
				if (!end) return now >= start;
				return now >= start && now < end;
			});
		})
	);
	readonly isSameMinute$ = combineLatest([this.activeTime$.asObservable(), this.now$]).pipe(
		map(([time, now]) =>
			time ? isSameMinute(new Date(time.start), roundToNearestMinutes(new Date(), { nearestTo: 1 })) : false
		)
	);
	readonly activeTime$tooltip = combineLatest([
		this.activeTime$.asObservable(),
		this.activeTime$stats,
		this.userSettingsQuery.select(),
		this.activeTime$times,
		this.scheduleType.asObservable(),
	]).pipe(
		switchMap(async ([active, stats, user, activeTimes, isWeekly]) => {
			if (!active) return null;
			const gaps =
				activeTimes
					?.map(({ type, ...x }) => {
						if (x.id === active.id) return null;
						return {
							time: new Date(x.start),
							type,
							isPause: type === "pause",
							typeName: "comego.types." + type,
						};
					})
					.filter(Boolean) || [];

			const now = startOfToday();
			const schedule = stats && getActiveSchedule(user, stats.start);
			const currentDay = now.getUTCDay();
			const times = isWeekly
				? this.comegoQuery
						.getAll({
							filterBy: (x) => {
								if (x.type !== "work") return false;
								const start = new Date(x.start);
								return (
									x.user.id === user.id,
									!isSameDay(start, now) &&
										isWithinInterval(start, {
											start: startOfWeek(now, { weekStartsOn: 1 }),
											end: endOfWeek(now, { weekStartsOn: 1 }),
										})
								);
							},
						})
						.map((x) => {
							return {
								...x,
								timeDiff: (new Date(x.end).getTime() - new Date(x.start).getTime()) / 1000,
							};
						})
				: [];
			const sched =
				stats &&
				schedule?.enabled &&
				parseScheduleStats(
					schedule as any,
					[
						...times,
						{
							start: stats.start.toISOString(),
							end: stats.start.toISOString(),
							timeDiff: stats.duration,
						} as any,
					],
					{
						calculateEveryday: true,
						allowDisabled: true,
						date: stats.start,
					}
				);

			if (sched && !sched.usage[currentDay]) sched.usage[currentDay] = { max: 0, used: 0 };
			else if (sched && !sched.isDayEnabled(currentDay)) sched.usage[currentDay].max = 0;
			const dateOfReference = startOfDay(now.getTime()),
				isPastSchedule = now.getTime() > dateOfReference.getTime(),
				currentDayUsage = sched?.usage[currentDay],
				pastFailure = isPastSchedule && currentDayUsage && currentDayUsage.used < currentDayUsage.max;

			return {
				...stats,
				schedule: sched && {
					...sched,
					current: {
						...currentDayUsage,
						percent: currentDayUsage.used / currentDayUsage.max,
					},
					isPastSchedule,
					pastFailure,
					graphState: pastFailure
						? "primary"
						: currentDayUsage.used < currentDayUsage.max
							? "accent"
							: "success",
					isWeekly: !!isWeekly,
				},
				savedGaps: gaps?.sort(firstBy((x) => x.time, "desc")),
			};
		})
	);
	toggleSchedGraph() {
		this.scheduleType.update((s) => !s);
	}
	readonly paused$ = this.activeTime$.asObservable().pipe(
		map((x) => {
			if (!x?.type) return null;
			return x.type === "pause";
		})
	);
	readonly tooltipOptions: NgxTippyProps = {
		onShow({ popper, reference }) {
			const refWidth = reference.getBoundingClientRect().width;
			if (refWidth) popper.style.width = `${refWidth}px`;
		},
		delay: [0, 500],
		offset: [0, -6],
		arrow: false,
		hideOnClick: false,
		interactive: true,
	};
	readonly lastVisit = createRxValue<Date>(null);
	readonly lastVisit$ = combineLatest([this.lastVisit.asObservable(), this.now$]).pipe(
		map(([visit, now]) => {
			if (!visit) return null;
			return {
				visit,
				duration: (now.getTime() - visit.getTime()) / 1000,
				now: now.getTime(),
			};
		})
	);
	ngOnInit(): void {
		const lastVisitData = this.appSettings.getValue()?.config?.last_visit as string;
		const lastVisit = lastVisitData && new Date(lastVisitData);
		const now = new Date();
		if (!lastVisit || !isValid(lastVisit) || !isSameDay(lastVisit, now))
			this.appSettings.updateByKey(`config.last_visit`, (this.lastVisit.value = new Date()).toISOString());
		else this.lastVisit.value = lastVisit;
	}
	ngOnDestroy(): void {
		if (this.cooldownClearHandle !== null) clearTimeout(this.cooldownClearHandle);
	}
	private armActionCooldown() {
		if (this.cooldownClearHandle !== null) clearTimeout(this.cooldownClearHandle);
		this.actionCooldown.update(true);
		this.cooldownClearHandle = setTimeout(() => {
			this.actionCooldown.update(false);
			this.cooldownClearHandle = null;
		}, ACTION_COOLDOWN_AFTER_CLICK_MS);
	}
	//disable recording feature to prevent overlap
	readonly disabledUntil = this.recordService.disabledUntil;
	readonly disableRecording = combineLatest([
		this.now$,
		this.disabledUntil.asObservable(),
		this.comegoQuery.selectAllFromCurrentUser({ filterBy: (x) => isSameDay(new Date(x.start), new Date()) }),
	]).pipe(
		map(([now, until, comegos]) => {
			if (!until || !comegos.length) return false;
			if (until) {
				const untilTime = coerceTimeDuration(until);
				if (untilTime <= now) this.disabledUntil.update(null);
				return untilTime > now;
			} else {
				const time = comegos.filter(
					(x) => x.type == "pause" && new Date(x.end) > now && new Date(x.start) < now
				)[0];
				if (!time) return false;
				this.disabledUntil.update(formatDate(time.end, "HH:mm", "en-US"));
			}
		})
	);
	private getOpenActiveComegoForCurrentUser(): ComegoTime | null {
		const userId = this.userSettingsQuery.getValue()?.id;
		if (!userId) return null;
		const rows = this.comegoQuery.getAll({
			filterBy: (x: any) => x.user?.id === userId && !x.end && !(x as ComegoTime & { deleted?: boolean }).deleted,
		}) as ComegoTime[];
		if (!rows.length) return null;
		return rows.sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime())[0];
	}
	private async handleStart(type?: ComegoTimeType) {
		const now = roundToNearestMinutes(new Date(), { nearestTo: 1 });
		const todayEntries = this.comegoQuery.getAll({
			filterBy: (x) => x.user?.id === this.userSettingsQuery.getValue().id && isSameDay(new Date(x.start), now),
		});
		const overlapping = todayEntries.some((entry) => {
			if (entry.type !== "work" && entry.type !== "pause") return false;
			const start = new Date(entry.start);
			const end = entry.end ? new Date(entry.end) : null;
			if (!end) return now >= start;
			return now >= start && now < end;
		});
		if (overlapping) {
			this.appService.notifyError("errors.times.comego.already_recording");
			return null;
		}
		const cuser = this.userSettingsQuery.getValue();
		const user = cuser.workspace.users.find((x) => x.id === cuser.id);
		const previousEntry = todayEntries
			.sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime())
			.find((x) => new Date(x.end) <= now);
		if (previousEntry?.type === "work" && type !== "pause") {
			if (!isSameMinute(new Date(previousEntry.end), now)) {
				const pauseData = {
					name: null,
					start: new Date(previousEntry.end),
					end: now,
					type: "pause",
					user,
				} as any;
				await this.comego.add(pauseData);
				this.appService.notifySuccess("comego.gapFilled");
			}
		} else if (previousEntry?.type === "pause" && type !== "pause" && previousEntry.end) {
			if (!isSameMinute(new Date(previousEntry.end), now)) {
				await this.comego.update({
					...previousEntry,
					end: now,
					user,
				} as any);
				this.appService.notifySuccess("comego.pauseAdjusted");
			}
		}
		const start = now;
		const checkinData = {
			name: null,
			start,
			end: null,
			type: type ?? "work",
			user,
		} as any;
		const times = await this.recordService.startCheckin(checkinData).catch(async (err: HttpErrorResponse) => {
			pushError(err);
			if (err.error.message == "errors.times.comego.breakRuleNotice-until") {
				const now = startOfMinute(new Date());
				const date = addMinutes(now, err.error.args.value);
				this.disabledUntil.update(formatDate(date, "HH:mm", "en-US"));
				// disabledUNtil.subscribe() => cunctruct date  if date == now  => enabled
			}
			return null;
		});
		log.debug(times);
		return times?.[0];
	}
	private async handleUpdate(state: "pause" | "play") {
		const time = this.getOpenActiveComegoForCurrentUser();
		if (!time) return;
		const tid = time.id;
		const stateConstraint = {
			pause: "pause",
			play: "work",
		}[state as string] as ComegoTimeType;
		let metaWorkingHoursError = false;
		const stopResult = await this.handleStop().catch(async (err: HttpErrorResponse) => {
			if (isWorkingHoursError(err.error) && stateConstraint === "pause") {
				if (isBreakRuleError(err.error)) metaWorkingHoursError = true;
				const entry = await asyncWrapTimeout(
					() =>
						toPromise(
							this.comegoQuery
								.selectAll({ filterBy: (x) => x.id === tid, limitTo: 1 })
								.pipe(map((x) => x[0]))
						),
					1000
				).catch(() => null);
				return { entry, appliedFix: false };
			}
			return { entry: null, appliedFix: false };
		});
		if (!stopResult.entry && !metaWorkingHoursError) return;
		if (tid) {
			// Pause/resume: release the dual-start link without stopping the project timer.
			await this.recordService.afterPairedWorkingHoursStopped(tid, false);
		}
		const shortBreakWarnArgs = state === "play" ? this.getShortBreakWarnArgs(stopResult.entry as ComegoTime) : null;
		const openAfterFix = this.getOpenActiveComegoForCurrentUser();
		if (stopResult.appliedFix && openAfterFix) {
			// Dialog already persisted the suggested outcome (e.g. active pause or resume work).
		} else {
			await this.handleStart(stateConstraint);
		}
		if (shortBreakWarnArgs) this.showShortBreakWarnDeferred(shortBreakWarnArgs);
		if (!stopResult.appliedFix) {
			this.appService.notifySuccess("success.created");
		}
	}
	/** Returns notification args if the ended time was a pause shorter than the workspace minimum; otherwise null. */
	private getShortBreakWarnArgs(endedTime: ComegoTime | null): { length: string } | null {
		if (!endedTime || endedTime.type !== "pause" || !endedTime.start || !endedTime.end) return null;
		const wh = this.userSettingsQuery.getValue()?.workspace?.schedules?.workspace?.["workinghours"] as
			| { minimumBreakDurationForCredit?: number | string }
			| undefined;
		const rawMin = wh?.minimumBreakDurationForCredit;
		let minBreakMs = 0;
		let minBreakMinutes = 0;
		if (typeof rawMin === "number" && rawMin > 0) {
			minBreakMs = rawMin * 3.6e6;
			minBreakMinutes = Math.round(rawMin * 60);
		} else if (typeof rawMin === "string" && rawMin) {
			const seconds = timeFormatToDuration(rawMin);
			if (seconds != null && seconds > 0) {
				minBreakMs = seconds * 1000;
				minBreakMinutes = Math.round(seconds / 60);
			}
		}
		if (minBreakMs <= 0) return null;
		const breakDurationMs = new Date(endedTime.end).getTime() - new Date(endedTime.start).getTime();
		if (breakDurationMs >= minBreakMs) return null;
		return { length: String(minBreakMinutes) };
	}
	private showShortBreakWarnDeferred(args: { length: string }) {
		this.appService.notifyInfo("errors.times.comego.shortBreak", undefined, args);
	}
	private async handleStop(): Promise<{ entry: ComegoTime | null; appliedFix: boolean }> {
		const time = this.getOpenActiveComegoForCurrentUser();
		if (!time) return { entry: null, appliedFix: false };
		return this.recordService.stopComegoWithRuleCheck(time, () => this.recordService.performNormalComegoStop(time));
	}
	private async handleManual(state?: "default" | "absence") {
		if (state === "absence") {
			await toPromise(
				this.dialog
					.open(ComeAndGoAbsenceDialogComponent, {
						data: {} as WorkingHoursAbsenceData,
					})
					.afterClosed()
			);
		} else {
			await toPromise(
				this.dialog
					.open(ComeAndGoCreateDialogComponent, {
						data: {} as WorkingHoursCreateData,
					})
					.afterClosed()
			);
		}
	}

	async handle(type: "start" | "stop" | "update" | "manual", payload?: any) {
		log.debug("handle", type, payload);
		if (this.loading$.value) return;
		this.armActionCooldown();
		this.loading$.update(true);
		try {
			if (type === "stop") {
				const pairedWhId = this.getOpenActiveComegoForCurrentUser()?.id;
				const stopResult = await Promise.resolve(this.handleStop()).catch((err) => {
					log.error(err);
					return { entry: null, appliedFix: false };
				});
				const warnArgs = this.getShortBreakWarnArgs(stopResult.entry);
				if (warnArgs) this.showShortBreakWarnDeferred(warnArgs);
				if (stopResult.entry != null && !stopResult.appliedFix) {
					this.appService.notifySuccess("success.created");
				}
				if (pairedWhId && stopResult.entry != null) {
					await this.recordService.afterPairedWorkingHoursStopped(pairedWhId, true);
				}
			} else {
				await Promise.resolve(this[`handle${capitalize(type)}`]?.(payload)).catch((err) => log.error(err));
			}
		} finally {
			this.loading$.update(false);
		}
	}
	updateWorkinghourDay() {
		this.dialog.open(ComeAndGoMultiUpdateDialogComponent, {
			data: {
				time: {
					value: [],
					date: new Date(this.activeTime$.value.start),
					refetchFromDate: true,
					forceRecreateDay: true,
				},
			},
		});
	}
}
